/**
 * DiffusionManagerModal — Modal UNIFIÉ « Gérer la diffusion » (iter95).
 *
 * Cette modale est ouverte par TOUS les points d'entrée (bouton « Diffuser » topbar,
 * carte Diffusion de la fiche, page globale). Elle remplace :
 *   - l'ancien composant components/DiffusionModal.jsx (social + IA post generation)
 *   - le composant LOT 3 initial (4 supports hardcodés)
 *
 * Elle couvre 3 sections :
 *   1. Portails immobiliers (toggles activate/deactivate) — filtrés par la config
 *      Super Admin de l'agence (agences.platforms).
 *   2. Site vitrine (toggle) — dérivé du lien 'website' de l'agence.
 *   3. Réseaux sociaux (avec « Générer le post » IA) — dérivés des liens social de l'agence.
 *
 * La liste des supports vient uniquement de la configuration agence :
 *   GET /api/marketing/agency-social-config → { links, platforms, website, agence_nom }
 * Aucune constante en dur pour l'activation. Un support non activé n'est pas proposé.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { activateSupport, deactivateSupport, refreshSelogerStatus, fetchLeboncoinMonitoring } from '../../services/diffusionApi';
// lot AL5 — seuil des 24 h : une seule définition pour tout le front.
import { attenteDepuis } from '../../constants/diffusionAttente';
import { useRelectureDiffusion } from '../../hooks/useRelectureDiffusion';
import { DIFFUSION_SUPPORTS, DIFFUSION_STATUS_CONFIG, normalizeDiffusion, filterEnabledSupports, etatDiffusionLisible, TON_ETAT } from '../../constants/diffusionConstants';
import { formatDateFR, formatPriceFR } from '../../utils/formatFR';
import { authHeaderOrEmpty } from '../../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const _authHeaders = () => authHeaderOrEmpty();

const socialPlatformInfo = {  linkedin:  { label: 'LinkedIn',       icon: 'bi-linkedin',    color: 'bg-blue-600' },
  facebook:  { label: 'Facebook',       icon: 'bi-facebook',    color: 'bg-blue-500' },
  instagram: { label: 'Instagram',      icon: 'bi-instagram',   color: 'bg-gradient-to-br from-purple-500 to-pink-500' },
  twitter:   { label: 'X (Twitter)',    icon: 'bi-twitter-x',   color: 'bg-black' },
  youtube:   { label: 'YouTube',        icon: 'bi-youtube',     color: 'bg-red-600' },
  tiktok:    { label: 'TikTok',         icon: 'bi-tiktok',      color: 'bg-black' },
  google_business: { label: 'Google Business', icon: 'bi-google', color: 'bg-blue-500' },
  whatsapp:  { label: 'WhatsApp',       icon: 'bi-whatsapp',    color: 'bg-green-500' },
};

const AI_TONES = [
  { id: 'professional', label: 'Professionnel', icon: 'bi-briefcase' },
  { id: 'casual', label: 'Décontracté', icon: 'bi-emoji-smile' },
  { id: 'enthusiastic', label: 'Enthousiaste', icon: 'bi-stars' },
];

const DiffusionManagerModal = ({ property, diffusion: diffusionProp, onClose, onChange }) => {
  const [state, setState] = useState(normalizeDiffusion(diffusionProp || property?.diffusion));
  const [busy, setBusy] = useState({});
  const [error, setError] = useState(null);
  // lot AH6 — retour de la lecture LeBonCoin à la demande.
  const [infoLbc, setInfoLbc] = useState(null);
  const [agencyConfig, setAgencyConfig] = useState({ links: [], platforms: [], supports: [], supportsConnectes: [], website: null, loading: true });
  const [tab, setTab] = useState('portails'); // 'portails' | 'social'
  const [socialSelected, setSocialSelected] = useState(null);
  const [socialTone, setSocialTone] = useState('professional');
  const [aiPost, setAiPost] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSaved, setAiSaved] = useState(false);

  // Charge la config agence
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/marketing/agency-social-config`, { headers: _authHeaders() });
        if (res.ok) {
          const data = await res.json();
          setAgencyConfig({
            links: data.links || [],
            platforms: data.platforms || [],
            supports: Array.isArray(data.supports) ? data.supports : [],
            // lot AC1 — liste des supports réellement branchés, fournie par le serveur
            // (diffusion_service.SUPPORTS_BRANCHES) : pas de seconde liste en dur ici.
            supportsConnectes: Array.isArray(data.supports_connectes) ? data.supports_connectes : [],
            website: data.website || null,
            loading: false,
          });
        } else {
          setAgencyConfig(c => ({ ...c, loading: false }));
        }
      } catch (e) {
        console.error('[diffusion] agency config load failed', e);
        setAgencyConfig(c => ({ ...c, loading: false }));
      }
    })();
  }, []);

  // Portails effectivement activés pour cette agence.
  // iter103 : Source de vérité UNIQUE — `data.supports` (calculé côté backend
  // via _get_agency_enabled_supports, identique à /diffusion/overview.supports).
  // Fin de la divergence : la modale et la page globale affichent EXACTEMENT
  // les mêmes portails, dérivés du même stockage `agences.platforms`.
  const enabledPortails = useMemo(() => {
    return filterEnabledSupports(agencyConfig.supports || []);
  }, [agencyConfig]);

  // Réseaux sociaux configurés
  const availableSocials = useMemo(() => {
    return (agencyConfig.links || [])
      .filter(l => l.type && socialPlatformInfo[l.type] && l.url)
      .map(l => ({ id: l.type, url: l.url, ...socialPlatformInfo[l.type] }));
  }, [agencyConfig]);

  const togglePortail = async (supportId) => {
    // lot AC3 — « en attente » = la demande est DÉJÀ partie chez le portail. Le geste inverse
    // est donc un retrait (annulation), jamais un second dépôt. Le serveur refuse de toute
    // façon un nouvel envoi tant que le statut n'est pas revenu (409 depot_en_cours).
    const statut = state[supportId]?.status || 'non_diffuse';
    // lot AD3 — pendant l'attente, l'interrupteur n'agit plus : une annulation vers le portail
    // ne doit pas se déclencher par un objet qui affiche « éteint ». L'action est nommée.
    if (statut === 'en_attente') return;
    // lot CG — « En ligne — mise à jour refusée » est EN LIGNE (statut `erreur` +
    // `en_ligne_avec_reserve`) : le geste inverse est un retrait. Règle lue dans
    // `etatDiffusionLisible`, ⛔ pas de copie locale.
    const dejaEnvoye = statut === 'diffuse'
      || etatDiffusionLisible(state[supportId] || {}).enLigneAvecReserve;
    setBusy(b => ({ ...b, [supportId]: true }));
    setError(null);
    try {
      const res = dejaEnvoye
        ? await deactivateSupport(property.id, supportId)
        : await activateSupport(property.id, supportId);
      setState(normalizeDiffusion(res.diffusion));
      if (typeof onChange === 'function') onChange(res.diffusion);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError((detail && typeof detail === 'object' ? detail.message : detail)
        || 'Impossible de mettre à jour ce support.');
    } finally {
      setBusy(b => ({ ...b, [supportId]: false }));
    }
  };

  // lot AC2 — le statut du portail est asynchrone : un bouton, déclenché par l'utilisateur.
  // PAS de rafraîchissement automatique ni de sondage périodique (cf. boucle de requêtes du lot AA).
  const rafraichirStatut = async (supportId) => {
    setBusy(b => ({ ...b, [`${supportId}:statut`]: true }));
    setError(null);
    try {
      const res = await refreshSelogerStatus(property.id);
      setState(normalizeDiffusion(res.diffusion));
      if (typeof onChange === 'function') onChange(res.diffusion);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError((detail && typeof detail === 'object' ? detail.message : detail)
        || 'Statut indisponible pour le moment.');
    } finally {
      setBusy(b => ({ ...b, [`${supportId}:statut`]: false }));
    }
  };

  // lot BM — SeLoger notifie tout seul : pendant l'attente, on relit NOTRE api (le bien),
  // jamais le portail. 30 s, 10 minutes au plus, intervalle nettoyé à la fermeture.
  const relireLeBien = async () => {
    try {
      const res = await fetch(`${API_URL}/api/properties/${property.id}`, { headers: _authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      setState(normalizeDiffusion(data.diffusion));
      if (typeof onChange === 'function') onChange(data.diffusion);
    } catch { /* silencieux : relecture d'agrément */ }
  };
  useRelectureDiffusion(state?.seloger?.status === 'en_attente', relireLeBien);

  // lot AL1 — mise à jour d'une annonce DÉJÀ en ligne : troisième action, distincte de
  // l'interrupteur (qui garde activer / retirer). On rejoue l'envoi existant : le
  // `classified_id` étant conservé (lot AC3), le connecteur part en PUT, sans perdre
  // l'ancienneté de l'annonce ni risquer un doublon.
  const mettreAJour = async (supportId) => {
    setBusy(b => ({ ...b, [supportId]: true }));
    setError(null);
    try {
      const res = await activateSupport(property.id, supportId);
      setState(normalizeDiffusion(res.diffusion));
      if (typeof onChange === 'function') onChange(res.diffusion);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError((detail && typeof detail === 'object' ? detail.message : detail)
        || 'Mise à jour impossible pour le moment.');
    } finally {
      setBusy(b => ({ ...b, [supportId]: false }));
    }
  };

  // lot AH6 — LeBonCoin ne renseigne l'état que par webhook : ce bouton interroge le
  // portail à la demande (leur lecture est limitée en fréquence : jamais de sondage).
  const releverLeboncoin = async (supportId) => {
    setBusy(b => ({ ...b, [`${supportId}:statut`]: true }));
    setError(null);
    setInfoLbc(null);
    try {
      const res = await fetchLeboncoinMonitoring(property.id);
      // lot AU3 — la relève CONCLUT côté serveur (erreur / en ligne / rien de sûr). On affiche
      // sa conclusion telle quelle : `status` et `state` n'existent pas dans la réponse du
      // portail, les afficher produisait un « état inconnu » systématique. Un champ qu'on ne
      // reçoit pas ne vaut pas « inconnu » : il vaut « nous n'avons pas l'information ».
      const conclusion = res?.conclusion || {};
      const prefixe = conclusion.statut === 'erreur' ? 'Dernier refus connu du portail'
        : conclusion.statut === 'diffuse' ? 'LeBonCoin — annonce en ligne'
          : 'LeBonCoin — rien de conclu';
      setInfoLbc(`${prefixe}${conclusion.message ? ` : ${conclusion.message}` : ''}`);
      // lot AM2-4a — la relève est un compte rendu d'AGENCE : sans cette relecture, l'écran
      // annonçait « annonce en ligne » en gardant son interrupteur gris, et l'utilisateur
      // croyait à un échec. On affiche ce que le serveur vient de conclure, pas l'état d'avant.
      if (res?.diffusion) {
        setState(normalizeDiffusion(res.diffusion));
        if (typeof onChange === 'function') onChange(res.diffusion);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError((detail && typeof detail === 'object' ? detail.message : detail)
        || 'Lecture LeBonCoin indisponible pour le moment.');
    } finally {
      setBusy(b => ({ ...b, [`${supportId}:statut`]: false }));
    }
  };

  // lot AD3 — l'annulation d'un dépôt en cours devient une action NOMMÉE, au lieu d'un
  // interrupteur qui affiche le contraire de ce qu'il fait. Même appel qu'auparavant.
  const annulerEnvoi = async (supportId) => {
    setBusy(b => ({ ...b, [supportId]: true }));
    setError(null);
    try {
      const res = await deactivateSupport(property.id, supportId);
      setState(normalizeDiffusion(res.diffusion));
      if (typeof onChange === 'function') onChange(res.diffusion);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError((detail && typeof detail === 'object' ? detail.message : detail)
        || 'Impossible d\u2019annuler l\u2019envoi.');
    } finally {
      setBusy(b => ({ ...b, [supportId]: false }));
    }
  };

  const generateAiPost = async () => {
    if (!socialSelected) return;
    setAiBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/marketing/generate-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ..._authHeaders() },
        body: JSON.stringify({ property_id: property.id, platform: socialSelected.id, tone: socialTone }),
      });
      if (res.ok) {
        const data = await res.json();
        setAiPost(data.post?.content || _fallbackPost(property, socialSelected));
      } else {
        setAiPost(_fallbackPost(property, socialSelected));
      }
    } catch {
      setAiPost(_fallbackPost(property, socialSelected));
    } finally {
      setAiBusy(false);
    }
  };

  const saveAiPost = async () => {
    if (!aiPost || !socialSelected) return;
    setAiBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/marketing/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ..._authHeaders() },
        body: JSON.stringify({
          property_id: property.id,
          property_reference: property.reference,
          platform: socialSelected.id,
          content: aiPost,
          status: 'draft',
        }),
      });
      if (res.ok) setAiSaved(true);
      else setError('Impossible d\u2019enregistrer le post.');
    } catch {
      setError('Impossible d\u2019enregistrer le post.');
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
      data-testid="diffusion-manager-overlay"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="diffusion-manager-modal"
      >
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/60">
          <div>
            <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <i className="bi bi-broadcast text-blue-600" /> Gérer la diffusion
            </h3>
            <p className="text-[12px] text-slate-500 mt-0.5 truncate">{property?.reference} — {property?.titre_annonce || property?.objet || 'Bien'}</p>
          </div>
          <button aria-label="Fermer" onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-200/60 text-slate-500">
            <i className="bi bi-x-lg" />
          </button>
        </div>

        {/* Onglets */}
        <div className="flex border-b border-slate-100 px-2 gap-1 pt-2">
          <button
            onClick={() => setTab('portails')}
            data-testid="diffusion-tab-portails"
            className={`px-3 py-2 text-[13px] font-semibold rounded-t-lg transition ${tab === 'portails' ? 'text-blue-700 border-b-2 border-blue-600' : 'text-slate-500 hover:text-slate-800'}`}
          >
            <i className="bi bi-house-fill mr-1.5" /> Portails & Site vitrine
            {enabledPortails.length > 0 && <span className="ml-1.5 text-[10px] text-slate-400">({enabledPortails.length})</span>}
          </button>
          <button
            onClick={() => setTab('social')}
            data-testid="diffusion-tab-social"
            className={`px-3 py-2 text-[13px] font-semibold rounded-t-lg transition ${tab === 'social' ? 'text-blue-700 border-b-2 border-blue-600' : 'text-slate-500 hover:text-slate-800'}`}
          >
            <i className="bi bi-share-fill mr-1.5" /> Réseaux sociaux
            {availableSocials.length > 0 && <span className="ml-1.5 text-[10px] text-slate-400">({availableSocials.length})</span>}
          </button>
        </div>

        {error && (
          <div className="mx-5 mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-[12.5px] text-red-700">
            <i className="bi bi-exclamation-triangle-fill mr-1.5" /> {error}
          </div>
        )}

        {agencyConfig.loading ? (
          <div className="py-12 text-center text-slate-500 flex-1">
            <i className="bi bi-arrow-repeat animate-spin text-2xl" />
            <p className="mt-2 text-sm">Chargement de la configuration…</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            {/* ═══════════ PORTAILS ═══════════ */}
            {tab === 'portails' && (
              <>
                {enabledPortails.length === 0 ? (
                  <div className="p-6 text-center" data-testid="diffusion-empty-portails">
                    <i className="bi bi-info-circle text-slate-400 text-3xl" />
                    <p className="mt-2 text-[13px] text-slate-600">Aucun support de diffusion activé pour votre agence.</p>
                    <p className="text-[12px] text-slate-500 mt-1">Contactez votre administrateur pour activer le module Diffusion.</p>
                  </div>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {enabledPortails.map(support => {
                      const st = state[support.id] || { status: 'non_diffuse', date: null, url: null };
                      // lot BP — MÊME table de libellés que la page Diffusion : « Publication
                      // en cours » / « Retrait en cours » / « En ligne », jamais « En attente »
                      // seul, qui ne disait pas ce qu'on attendait.
                      const lisible = etatDiffusionLisible(st);
                      const cfg = TON_ETAT[lisible.ton] || TON_ETAT.muet;
                      const isOn = st.status === 'diffuse' || lisible.enLigneAvecReserve;
                      const enAttente = st.status === 'en_attente';
                      const sansReponseDepuis = enAttente ? attenteDepuis(st.date_depot) : null;
                      // lot AU15/AU17 — deux attentes très différentes portent le même statut :
                      // un DÉPÔT qui attend la mise en ligne, et un RETRAIT déjà parti chez le
                      // portail. `attente_retrait` les distingue ; sans lui l'écran proposait
                      // « Annuler l'envoi », qui relançait un DELETE fautif.
                      const attenteRetrait = enAttente && !!st.attente_retrait;
                      // lot AL5 — au-delà du seuil sans réponse, l'interrupteur REDEVIENT
                      // actionnable : un portail muet (429, retrait qui traîne) enfermait
                      // définitivement le bien. Même seuil que la levée de garde serveur.
                      const verrouille = enAttente && !sansReponseDepuis;
                      const supportBusy = !!busy[support.id];
                      const statutBusy = !!busy[`${support.id}:statut`];
                      // lot AC1 — un support sans connecteur ne diffuse rien : on le dit sur SA
                      // ligne, l'interrupteur y vaut « suivi manuel » et non « en ligne ».
                      const connecte = (agencyConfig.supportsConnectes || []).includes(support.id);
                      return (
                        <li key={support.id} className="px-5 py-4 flex items-start gap-3" data-testid={`diffusion-manager-row-${support.id}`}>
                          <span className={`inline-flex w-9 h-9 rounded-lg items-center justify-center ${support.accent} ring-1 ${support.ring} bg-white shrink-0`}>
                            <i className={`bi ${support.icon}`} />
                          </span>
                          <div className="flex-1 min-w-0 text-left">
                            <div className="text-[13px] font-semibold text-slate-900">{support.label}</div>
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                              <span className={`inline-flex items-center gap-1.5 text-[11.5px] font-medium ${cfg.text}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} aria-hidden="true" />
                                {connecte || st.status !== 'diffuse'
                                  ? (lisible.label || DIFFUSION_STATUS_CONFIG.non_diffuse.label)
                                  : 'Marqué diffusé (suivi manuel)'}
                              </span>
                              {st.date && <span className="text-[10.5px] text-slate-400 tabular-nums">· {formatDateFR(st.date)}</span>}
                              {/* lot BM — pour SeLoger, l'URL vient de la notification
                                  `urlCallback` : on la propose dès qu'on l'a, même en attente. */}
                              {st.url && (isOn || support.id === 'seloger') && (
                                <a href={st.url} target="_blank" rel="noreferrer noopener"
                                   data-testid={`diffusion-portal-link-${support.id}`}
                                   className="text-[11px] text-blue-600 hover:underline">
                                  · {support.id === 'seloger' ? 'Voir sur SeLoger' : 'Voir l\u2019annonce'}
                                </a>
                              )}
                            </div>
                            {!connecte && (
                              <p className="text-[11px] text-slate-500 mt-1" data-testid={`diffusion-no-connector-${support.id}`}>
                                <i className="bi bi-plug mr-1" />
                                Pas de connecteur : cet interrupteur ne fait que noter votre dépôt manuel sur le portail.
                              </p>
                            )}
                            {st.message && !attenteRetrait && (
                              <p
                                className={`text-[11px] mt-1 ${st.status === 'erreur' ? 'text-red-700' : 'text-slate-500'}`}
                                data-testid={`diffusion-manager-message-${support.id}`}
                              >
                                {st.message}
                              </p>
                            )}
                            {/* lot AC2 — statut asynchrone : consultable à la demande, jamais en boucle.
                                lot AD3 — l'annulation devient une action nommée, à côté. */}
                            {isOn && ['seloger', 'leboncoin'].includes(support.id) && (
                              <div className="mt-1.5">
                                <button
                                  type="button"
                                  onClick={() => mettreAJour(support.id)}
                                  disabled={supportBusy}
                                  data-testid={`diffusion-update-${support.id}`}
                                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-slate-200 text-[11.5px] font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                                >
                                  <i className="bi bi-arrow-up-circle" />
                                  {supportBusy ? 'Mise à jour…' : 'Mettre à jour l\u2019annonce'}
                                </button>
                              </div>
                            )}
                            {enAttente && support.id === 'seloger' && (
                              <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                                {/* lot BP — disponible SANS DÉLAI : c'est le geste qui débloque
                                    une attente, il ne doit pas se cacher 30 minutes. */}
                                {!!st.classified_id && (
                                <button
                                  type="button"
                                  onClick={() => rafraichirStatut(support.id)}
                                  disabled={statutBusy}
                                  data-testid={`diffusion-refresh-status-${support.id}`}
                                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-slate-200 text-[11.5px] font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                                >
                                  <i className={`bi bi-arrow-repeat ${statutBusy ? 'animate-spin' : ''}`} />
                                  {statutBusy ? 'Relève du statut…' : 'Rafraîchir le statut'}
                                </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => annulerEnvoi(support.id)}
                                  disabled={supportBusy}
                                  data-testid={`diffusion-cancel-send-${support.id}`}
                                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-slate-200 text-[11.5px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                >
                                  <i className="bi bi-x-circle" />
                                  {supportBusy ? 'Annulation…' : 'Annuler l\u2019envoi'}
                                </button>
                              </div>
                            )}
                            {enAttente && support.id === 'leboncoin' && (
                              <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                                <button
                                  type="button"
                                  onClick={() => releverLeboncoin(support.id)}
                                  disabled={statutBusy}
                                  data-testid={`diffusion-refresh-status-${support.id}`}
                                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-slate-200 text-[11.5px] font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                                >
                                  <i className={`bi bi-arrow-repeat ${statutBusy ? 'animate-spin' : ''}`} />
                                  {statutBusy ? 'Relève du statut…' : 'Relever le statut'}
                                </button>
                                {!attenteRetrait && (
                                <button
                                  type="button"
                                  onClick={() => annulerEnvoi(support.id)}
                                  disabled={supportBusy}
                                  data-testid={`diffusion-cancel-send-${support.id}`}
                                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-slate-200 text-[11.5px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                >
                                  <i className="bi bi-x-circle" />
                                  {supportBusy ? 'Annulation…' : 'Annuler l\u2019envoi'}
                                </button>
                                )}
                              </div>
                            )}
                            {attenteRetrait && support.id === 'leboncoin' && !sansReponseDepuis && (
                              <p className="text-[11px] text-slate-600 mt-1"
                                 data-testid="diffusion-leboncoin-attente-retrait">
                                <i className="bi bi-hourglass-split mr-1" />
                                Retrait demandé{st.date_depot ? ` le ${formatDateFR(st.date_depot)}` : ''} — LeBonCoin
                                confirmera la suppression. Relevez le statut pour vérifier.
                              </p>
                            )}
                            {infoLbc && support.id === 'leboncoin' && (
                              <p className="text-[11px] text-slate-600 mt-1"
                                 data-testid="diffusion-leboncoin-monitoring-info">
                                {infoLbc}
                              </p>
                            )}
                            {enAttente && sansReponseDepuis && (
                              <p
                                className="text-[11px] text-amber-700 mt-1"
                                data-testid={`diffusion-waiting-since-${support.id}`}
                              >
                                <i className="bi bi-hourglass-split mr-1" />
                                Sans réponse du portail depuis {sansReponseDepuis}
                                {st.date_depot && (attenteRetrait
                                  ? ` (retrait demandé le ${formatDateFR(st.date_depot)})`
                                  : ` (déposée le ${formatDateFR(st.date_depot)})`)}.
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => togglePortail(support.id)}
                            disabled={supportBusy || verrouille}
                            data-testid={`diffusion-toggle-${support.id}`}
                            aria-pressed={isOn}
                            title={verrouille
                              ? (attenteRetrait
                                  ? 'Retrait en cours chez le portail — relevez le statut pour le confirmer'
                                  : 'Demande en cours chez le portail — utilisez « Annuler l\u2019envoi »')
                              : (isOn ? 'Retirer du portail' : 'Diffuser sur ce support')}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 mt-0.5 ${isOn ? 'bg-emerald-500' : 'bg-slate-300'} disabled:opacity-60`}
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${isOn ? 'translate-x-6' : 'translate-x-1'}`} />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </>
            )}

            {/* ═══════════ RÉSEAUX SOCIAUX ═══════════ */}
            {tab === 'social' && (
              <div className="p-5 space-y-4">
                {availableSocials.length === 0 ? (
                  <div className="text-center py-8" data-testid="diffusion-empty-social">
                    <i className="bi bi-info-circle text-slate-400 text-3xl" />
                    <p className="mt-2 text-[13px] text-slate-600">Aucun réseau social configuré pour votre agence.</p>
                    <p className="text-[12px] text-slate-500 mt-1">Contactez votre administrateur pour renseigner vos réseaux (Instagram, Facebook, LinkedIn…).</p>
                  </div>
                ) : (
                  <>
                    <div>
                      <p className="text-[12px] font-semibold text-slate-700 mb-2">1. Choisissez le réseau</p>
                      <div className="grid grid-cols-2 gap-2">
                        {availableSocials.map(s => (
                          <button
                            key={s.id}
                            onClick={() => { setSocialSelected(s); setAiPost(''); setAiSaved(false); }}
                            data-testid={`diffusion-social-${s.id}`}
                            className={`p-3 rounded-xl border-2 transition flex items-center gap-2 text-left ${socialSelected?.id === s.id ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}
                          >
                            <span className={`w-8 h-8 ${s.color} text-white rounded-lg flex items-center justify-center`}>
                              <i className={`${s.icon} text-sm`} />
                            </span>
                            <span className="text-[12.5px] font-medium text-slate-800">{s.label}</span>
                            {socialSelected?.id === s.id && <i className="bi bi-check-circle-fill text-blue-500 ml-auto" />}
                          </button>
                        ))}
                      </div>
                    </div>

                    {socialSelected && (
                      <>
                        <div>
                          <p className="text-[12px] font-semibold text-slate-700 mb-2">2. Ton du message</p>
                          <div className="flex gap-2">
                            {AI_TONES.map(t => (
                              <button
                                key={t.id}
                                onClick={() => setSocialTone(t.id)}
                                data-testid={`diffusion-tone-${t.id}`}
                                className={`flex-1 px-2 py-2 rounded-lg border transition text-center ${socialTone === t.id ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-700 hover:border-slate-300'}`}
                              >
                                <i className={`bi ${t.icon} text-sm mr-1`} />
                                <span className="text-[12px] font-medium">{t.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>

                        {!aiPost && (
                          <button
                            type="button"
                            onClick={generateAiPost}
                            disabled={aiBusy}
                            data-testid="diffusion-generate-post-btn"
                            className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg font-semibold text-[13px] transition flex items-center justify-center gap-2"
                          >
                            {aiBusy ? (<><i className="bi bi-arrow-repeat animate-spin" /> Génération IA…</>) : (<><i className="bi bi-magic" /> Générer le post</>)}
                          </button>
                        )}

                        {aiPost && (
                          <>
                            <textarea
                              value={aiPost}
                              onChange={(e) => { setAiPost(e.target.value); setAiSaved(false); }}
                              data-testid="diffusion-post-textarea"
                              className="w-full h-40 p-3 border border-slate-200 rounded-lg resize-none text-[13px] focus:ring-2 focus:ring-blue-500"
                            />
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => { navigator.clipboard.writeText(aiPost); }}
                                className="flex-1 py-2 px-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-[12.5px] font-medium"
                              >
                                <i className="bi bi-clipboard mr-1" /> Copier
                              </button>
                              <button
                                type="button"
                                onClick={saveAiPost}
                                disabled={aiBusy}
                                data-testid="diffusion-save-post-btn"
                                className="flex-1 py-2 px-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white rounded-lg text-[12.5px] font-semibold"
                              >
                                {aiSaved ? (<><i className="bi bi-check2 mr-1" /> Enregistré</>) : (<><i className="bi bi-cloud-arrow-up mr-1" /> Enregistrer</>)}
                              </button>
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/40 text-[11px] text-slate-500 flex items-center gap-1.5">
          <i className="bi bi-info-circle" />
          Les supports actifs proviennent de la config agence (Super Admin → Diffusion).
        </div>
      </div>
    </div>
  );
};

function _fallbackPost(property, platform) {
  const title = property.titre_annonce || property.reference || 'Bien immobilier';
  const ville = property.bien_general?.ville_generale || property.ville || '';
  const surface = property.composition?.superficie_totale || '';
  const prix = property.informations_vente?.prix_cession || property.informations_bail?.loyer_mensuel || '';
  let post = `🏢 NOUVELLE OPPORTUNITÉ !\n\n${title}\n`;
  if (ville) post += `📍 ${ville}\n`;
  if (surface) post += `📐 ${surface} m²\n`;
  if (prix) post += `💰 ${formatPriceFR(parseInt(prix))}\n`;
  post += `\nContactez-nous pour plus d'informations !\n\n#immobilier #commercial #${(ville || '').toLowerCase().replace(/\s+/g, '')}`;
  return post;
}

export default DiffusionManagerModal;
